import Badge from "../ui/badge/Badge";
import Button from "../ui/button/Button";
import { NMWellData } from "../../lib/api/nm";

interface NMWellCardProps {
  well: NMWellData;
  onImport?: () => void;
  importing?: boolean;
}

/**
 * Get status badge color based on well status
 */
function getStatusColor(status: string): "success" | "info" | "light" | "warning" | "error" {
  const statusLower = status.toLowerCase();
  if (statusLower.includes("producing")) return "success";
  if (statusLower.includes("drilling")) return "info";
  if (statusLower.includes("plugged")) return "light";
  if (statusLower.includes("shut")) return "warning";
  if (statusLower.includes("abandoned")) return "error";
  return "light";
}

/**
 * Get well type badge color
 */
function getWellTypeColor(wellType: string): "primary" | "info" | "warning" {
  const typeLower = wellType.toLowerCase();
  if (typeLower.includes("oil")) return "primary";
  if (typeLower.includes("gas")) return "info";
  return "warning";
}

/**
 * Format date string to readable format
 */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return "N/A";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format number with commas
 */
function formatNumber(num: number | null): string {
  if (num === null) return "N/A";
  return num.toLocaleString();
}

export default function NMWellCard({ well, onImport, importing = false }: NMWellCardProps) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-white/[0.05]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              {well.well_name || "Unnamed Well"}
            </h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                API: {well.api10}
              </span>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {well.api14}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge color={getStatusColor(well.status)} size="sm">
              {well.status}
            </Badge>
            {well.well_type && (
              <Badge color={getWellTypeColor(well.well_type)} variant="solid" size="sm">
                {well.well_type}
              </Badge>
            )}
            {well.direction && (
              <Badge color="light" size="sm">
                {well.direction}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {/* Operator Info */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Operator
            </h4>
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {well.operator_name || "N/A"}
            </p>
            {well.operator_number && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Operator # {well.operator_number}
              </p>
            )}
          </div>

          {/* Location */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Location
            </h4>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {well.surface_location || "N/A"}
            </p>
            {(well.latitude !== null || well.longitude !== null) && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {well.latitude !== null ? well.latitude.toFixed(6) : "N/A"}, {well.longitude !== null ? well.longitude.toFixed(6) : "N/A"}
                {well.datum ? ` (${well.datum})` : ""}
              </p>
            )}
          </div>

          {/* Formation */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Formation
            </h4>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {well.formation || "N/A"}
            </p>
            {well.proposed_formation && well.proposed_formation !== well.formation && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Proposed: {well.proposed_formation}
              </p>
            )}
          </div>

          {/* Depth Info */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Depth Information
            </h4>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Proposed:</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {well.proposed_depth_ft !== null && well.proposed_depth_ft !== undefined ? `${formatNumber(well.proposed_depth_ft)} ft` : "N/A"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">TVD:</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {(well.true_vertical_depth_ft ?? well.tvd_ft) !== null ? `${formatNumber(well.true_vertical_depth_ft ?? well.tvd_ft)} ft` : "N/A"}
                </span>
              </div>
              {well.measured_vertical_depth_ft !== null && well.measured_vertical_depth_ft !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">MVD:</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {formatNumber(well.measured_vertical_depth_ft)} ft
                  </span>
                </div>
              )}
              {well.plugback_measured_ft !== null && well.plugback_measured_ft !== undefined && well.plugback_measured_ft > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Plugback:</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {formatNumber(well.plugback_measured_ft)} ft
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">GL Elevation:</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {(well.gl_elevation_ft ?? well.elevation_ft) !== null ? `${formatNumber(well.gl_elevation_ft ?? well.elevation_ft)} ft` : "N/A"}
                </span>
              </div>
              {well.kb_elevation_ft !== null && well.kb_elevation_ft !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">KB Elevation:</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {formatNumber(well.kb_elevation_ft)} ft
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Event Dates */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Key Dates
            </h4>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Spud:</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {formatDate(well.event_dates?.spud_date ?? well.spud_date)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Completion:</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {formatDate(well.event_dates?.completion_date ?? well.completion_date)}
                </span>
              </div>
              {well.event_dates?.first_production_date && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">First Prod:</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {formatDate(well.event_dates.first_production_date)}
                  </span>
                </div>
              )}
              {well.event_dates?.initial_apd_approval && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">APD Approval:</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {formatDate(well.event_dates.initial_apd_approval)}
                  </span>
                </div>
              )}
              {well.event_dates?.last_inspection && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Last Inspection:</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {formatDate(well.event_dates.last_inspection)}
                  </span>
                </div>
              )}
              {well.event_dates?.plugging_date && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Plugging:</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {formatDate(well.event_dates.plugging_date)}
                  </span>
                </div>
              )}
              {well.event_dates?.ta_date && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">TA Date:</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {formatDate(well.event_dates.ta_date)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Info */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Additional Info
            </h4>
            <div className="space-y-1">
              {well.work_type && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Work Type:</span>
                  <span className="text-gray-700 dark:text-gray-300">{well.work_type}</span>
                </div>
              )}
              {well.mineral_owner && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Mineral Owner:</span>
                  <span className="text-gray-700 dark:text-gray-300">{well.mineral_owner}</span>
                </div>
              )}
              {well.surface_owner && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Surface Owner:</span>
                  <span className="text-gray-700 dark:text-gray-300">{well.surface_owner}</span>
                </div>
              )}
              {well.sing_mult_compl && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Compl Type:</span>
                  <span className="text-gray-700 dark:text-gray-300">{well.sing_mult_compl}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Casing Records Table */}
        {well.casing_records && well.casing_records.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-white/[0.05]">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Casing Strings ({well.casing_records.length})
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Type</th>
                    <th className="text-right py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">OD (in)</th>
                    <th className="text-right py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Bottom (ft)</th>
                    <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Grade</th>
                    <th className="text-right py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Weight</th>
                    <th className="text-right py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Cement Top</th>
                    <th className="text-right py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Sacks</th>
                  </tr>
                </thead>
                <tbody>
                  {well.casing_records.map((casing, idx) => (
                    <tr key={idx} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 px-2 text-gray-800 dark:text-gray-200 font-medium">{casing.string_type}</td>
                      <td className="py-2 px-2 text-right text-gray-700 dark:text-gray-300">{casing.diameter_in ?? "—"}</td>
                      <td className="py-2 px-2 text-right text-gray-700 dark:text-gray-300">{casing.bottom_ft !== null ? formatNumber(casing.bottom_ft) : "—"}</td>
                      <td className="py-2 px-2 text-gray-700 dark:text-gray-300">{casing.grade ?? "—"}</td>
                      <td className="py-2 px-2 text-right text-gray-700 dark:text-gray-300">{casing.weight_ppf ? `${casing.weight_ppf} ppf` : "—"}</td>
                      <td className="py-2 px-2 text-right text-gray-700 dark:text-gray-300">{casing.cement_top_ft !== null ? formatNumber(casing.cement_top_ft) : "—"}</td>
                      <td className="py-2 px-2 text-right text-gray-700 dark:text-gray-300">{casing.cement_sacks ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Completions with Perforations */}
        {well.completions && well.completions.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-white/[0.05]">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Completions ({well.completions.length})
            </h4>
            <div className="space-y-3">
              {well.completions.map((comp, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {comp.completion_name || `Completion ${comp.completion_id}`}
                    </p>
                    {comp.status && (
                      <Badge color={comp.status.toLowerCase().includes("active") ? "success" : "light"} size="sm">
                        {comp.status}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    {comp.production_method && <span>Method: {comp.production_method}</span>}
                    {comp.last_produced && <span>Last Produced: {comp.last_produced}</span>}
                  </div>
                  {comp.perforations && comp.perforations.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Perforations:</p>
                      <div className="flex flex-wrap gap-2">
                        {comp.perforations.map((perf, pidx) => (
                          <span key={pidx} className="inline-block bg-gray-100 dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">
                            {perf.top_md_ft}–{perf.bottom_md_ft} ft MD
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Formation Tops */}
        {well.formation_tops && well.formation_tops.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-white/[0.05]">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Formation Tops ({well.formation_tops.length})
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Formation</th>
                    <th className="text-right py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Top (ft)</th>
                    <th className="text-center py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Producing</th>
                    <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Method</th>
                  </tr>
                </thead>
                <tbody>
                  {well.formation_tops.map((ft, idx) => (
                    <tr key={idx} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 px-2 text-gray-800 dark:text-gray-200 font-medium">{ft.formation_name}</td>
                      <td className="py-2 px-2 text-right text-gray-700 dark:text-gray-300">{ft.top_ft !== null ? formatNumber(ft.top_ft) : "—"}</td>
                      <td className="py-2 px-2 text-center text-gray-700 dark:text-gray-300">{ft.producing === true ? "Yes" : ft.producing === false ? "No" : "—"}</td>
                      <td className="py-2 px-2 text-gray-700 dark:text-gray-300">{ft.method_obtained ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Footer with Import Button */}
      {onImport && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-white/[0.05] bg-gray-50 dark:bg-white/[0.02] rounded-b-2xl">
          <div className="flex justify-end">
            <Button
              onClick={onImport}
              disabled={importing}
              size="sm"
            >
              {importing ? (
                <>
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"></span>
                  Importing...
                </>
              ) : (
                "Import to System"
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
